import { defineComponent, reactive, computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import _ from 'lodash';
import moment from 'moment';
import { Form } from 'vee-validate';
import * as yup from 'yup';
import { useMessageBox, withLoading } from '@hems/component';
import { DeviceServiceUser, CommonService } from '@hems/service';
import { isUS } from '@hems/user/src/config/constants';
import EnergyBackupSettingPvModuleGen2 from '@hems/user/src/containers/popup/homeinfo/control/EnergyBackupSettingPvModuleGen2.vue';
import EnergyBackupSettingPvModuleGen3SmartModuleGen2 from '@hems/user/src/containers/popup/homeinfo/control/EnergyBackupSettingPvModuleGen3SmartModuleGen2.vue';
import EnergyPolicySetting from '@hems/user/src/containers/popup/homeinfo/control/EnergyPolicySetting.vue';
import StormCoverSetting from '@hems/user/src/containers/popup/homeinfo/control/StormCoverSetting.vue';
import { Helper, DateHelper } from '@hems/util';
import { useACDeviceType, useGenType } from '@hems/util/src/composable';
import { EMS_TYPE, GEN_TYPE, SETTING_TARGET, SETTING_TYPE } from '@hems/util/src/constant';
import { ENERGY_POLICY } from '@hems/util/src/constant/constant';
import { AxiosErrorException, STATUS_CODE } from '@hems/util/src/exception/exception';
import { isEnergyPolicyType } from '@hems/util/src/helper/tsguardHelper';
const DEFAULT_ERROR_CODE = '-1';
export default defineComponent({
    name: 'Control',
    components: {
        EnergyBackupSettingPvModuleGen2,
        EnergyBackupSettingPvModuleGen3SmartModuleGen2,
        EnergyPolicySetting,
        Form,
        StormCoverSetting,
    },
    emits: ['close'],
    setup() {
        const { t } = useI18n();
        const deviceServiceUser = new DeviceServiceUser(window.axiosInstance.axios);
        const commonService = new CommonService(window.axiosInstance.axios);
        const store = useStore();
        const messageBox = useMessageBox();
        const state = reactive({
            isInit: false,
            copyData: null,
            data: null,
            editable: false,
            isTimeBased: false,
            batteryGen2: { backup_soc: 0 },
            batteryGen3: { battery_backup_soc: 0 },
            energyPolicy: null,
            stormCoverSetting: { storm_cover_flag: 0, storm_cover_charge_type: null },
        });
        const { genType, isACSystem } = useGenType();
        const { isSolarConfig } = useACDeviceType();
        const siteId = computed(() => Number(store.state.user.homeId));
        const deviceId = computed(() => store.state.user.ownDeviceId);
        const isEnergyBackupSetting = computed(() => !isACSystem || (isACSystem && store.state.device.siteConfiguration?.hasESS));
        const currentEnergyControl = ref(null);
        const isStormCoverActivated = computed(() => {
            return currentEnergyControl.value === ENERGY_POLICY.STORM_COVER;
        });
        const isStormCoverSupportedDevice = computed(() => (store.state.device.siteConfiguration?.hasHub && isACSystem) ?? false);
        const getDeviceSettingInfo = async () => {
            try {
                const data = isUS
                    ? await deviceServiceUser.getUserACSystemSettingInfo(siteId.value)
                    : await deviceServiceUser.getSettingInfo(siteId.value);
                state.data = data;
                state.copyData = data;
                state.batteryGen2 = { backup_soc: state.data.backup_soc };
                state.batteryGen3 = { battery_backup_soc: state.data.battery_backup_soc };
                state.energyPolicy = { ...state.data };
                state.stormCoverSetting = {
                    storm_cover_flag: state.data.storm_cover_flag,
                    storm_cover_charge_type: state.data.storm_cover_charge_type,
                };
            }
            catch (e) {
                console.error(e);
            }
        };
        const getSettingsType = () => {
            if (!(genType.value === GEN_TYPE.GEN2) &&
                !isACSystem &&
                state.energyPolicy?.energy_policy === parseInt(ENERGY_POLICY.TIME_BASED, 10)) {
                return { msg_type: SETTING_TYPE.BASIC_TOU };
            }
            return { msg_type: SETTING_TYPE.BASIC };
        };
        const getErrorMessageByGenType = (errorCode = DEFAULT_ERROR_CODE, errorMessage = '') => {
            const code = Number(errorCode);
            switch (genType.value) {
                case GEN_TYPE.GEN2:
                    return {
                        errorCode: code,
                        // FIXME: 정의되지 않은 에러 코드로 들어올 때는 서버에서 255메세지(알 수 없는 오류)로 받을 수 있도록 요청 처리 후 defaultValue 제거
                        errorMessage: t(`code.cmdgen2configreqstatuscd.${errorCode}`, {
                            defaultValue: errorMessage,
                        }),
                    };
                case GEN_TYPE.GEN3:
                case GEN_TYPE.FOX_ESS:
                case GEN_TYPE.FOX_ESS_H3:
                    return {
                        errorCode: code,
                        // FIXME: 정의되지 않은 에러 코드로 들어올 때는 서버에서 255메세지(알 수 없는 오류)로 받을 수 있도록 요청 처리 후 defaultValue 제거
                        errorMessage: t(`code.cmdgen3configreqstatuscd.${errorCode}`, {
                            defaultValue: errorMessage,
                        }),
                    };
                default:
                    return {
                        errorCode: code,
                        errorMessage,
                    };
            }
        };
        const updateGen2Setting = async () => {
            const settingParams = getSettingsType();
            const gen2Params = { ...settingParams, backup_soc: state.batteryGen2.backup_soc };
            const result = await deviceServiceUser.controlGen2Setting(deviceId.value, gen2Params);
            if (result.status === STATUS_CODE.OK) {
                messageBox.alert(t('message.save_success')).open();
            }
            else {
                const errorInfo = getErrorMessageByGenType(result.errorCode, result.message);
                openErrorMessageBox(errorInfo.errorCode, errorInfo.errorMessage);
            }
        };
        const getTouSettingsParams = () => {
            return {
                energy_policy: state.energyPolicy?.energy_policy,
                tou_start_date: state.energyPolicy?.tou_start_date,
                tou_end_date: state.energyPolicy?.tou_end_date,
                tou_weekday_count: state.energyPolicy?.tou_weekday_count,
                tou_weekday_section: state.energyPolicy?.tou_weekday_section,
                tou_weekend_count: state.energyPolicy?.tou_weekend_count,
                tou_weekend_section: state.energyPolicy?.tou_weekend_section,
            };
        };
        const getGen3SettingsParams = () => {
            const settingType = getSettingsType();
            if (settingType.msg_type === SETTING_TYPE.BASIC_TOU) {
                return {
                    ...settingType,
                    ...getTouSettingsParams(),
                    battery_backup_soc: state.batteryGen3.battery_backup_soc,
                };
            }
            return {
                ...settingType,
                battery_backup_soc: state.batteryGen3.battery_backup_soc,
                energy_policy: state.energyPolicy?.energy_policy,
            };
        };
        const updateGen3Setting = async () => {
            const settingParams = getGen3SettingsParams();
            const result = await deviceServiceUser.controlGen3Setting(deviceId.value, settingParams);
            if (result.status === STATUS_CODE.OK) {
                messageBox.alert(t('message.save_success')).open();
            }
            else {
                const errorInfo = getErrorMessageByGenType(result.errorCode, result.message);
                openErrorMessageBox(errorInfo.errorCode, errorInfo.errorMessage);
            }
        };
        const ACESSettingParam = computed(() => ({
            msg_type: SETTING_TYPE.BASIC,
            battery_backup_soc: state.batteryGen3.battery_backup_soc,
        }));
        const stormCoverSettingParams = computed(() => ({
            msg_type: SETTING_TYPE.BASIC,
            storm_cover_flag: state.stormCoverSetting.storm_cover_flag,
            storm_cover_charge_type: state.stormCoverSetting.storm_cover_charge_type,
        }));
        const updateACSystemSetting = async () => {
            if (isEnergyBackupSetting.value && !isStormCoverActivated.value) {
                await deviceServiceUser.controlACSystemSettingWithSiteId(siteId.value, ACESSettingParam.value, SETTING_TARGET.AC_ESS);
            }
            if (isStormCoverSupportedDevice.value) {
                await deviceServiceUser.controlACSystemSettingWithSiteId(siteId.value, stormCoverSettingParams.value, SETTING_TARGET.SITE);
            }
            messageBox.alert(t('message.save_success')).open();
        };
        const saveSetting = async () => {
            const isOk = await messageBox.confirm(t('message.apply_set_value')).open();
            if (isOk) {
                withLoading(async () => {
                    const gen3TypeGroup = [GEN_TYPE.GEN3, GEN_TYPE.FOX_ESS, GEN_TYPE.FOX_ESS_H3];
                    try {
                        if (genType.value === GEN_TYPE.GEN2) {
                            await updateGen2Setting();
                        }
                        else if (genType.value && gen3TypeGroup.includes(genType.value)) {
                            await updateGen3Setting();
                        }
                        else if (genType.value === GEN_TYPE.AC_SYS) {
                            await updateACSystemSetting();
                        }
                    }
                    catch (e) {
                        console.error(e);
                        if (e instanceof AxiosErrorException) {
                            const errorCode = e.data.errorCode;
                            const errorMessage = e.data.message;
                            const status = e.afterError({
                                500: async () => {
                                    const errorInfo = getErrorMessageByGenType(errorCode, errorMessage);
                                    openErrorMessageBox(errorInfo.errorCode, errorInfo.errorMessage);
                                },
                                400: async () => {
                                    const errorInfo = getErrorMessageByGenType(errorCode, errorMessage);
                                    openErrorMessageBox(errorInfo.errorCode, errorInfo.errorMessage);
                                },
                            });
                            if (status)
                                return;
                            openErrorMessageBox();
                            return;
                        }
                        openErrorMessageBox();
                        return;
                    }
                })();
            }
        };
        const openErrorMessageBox = (errorCode = Number(DEFAULT_ERROR_CODE), errorMessage = '') => {
            if (errorCode === Number(DEFAULT_ERROR_CODE) || errorMessage === '') {
                messageBox.alert(t('message.error_apply_setting')).open();
            }
            else {
                // @TODO: 세팅 errorCode 상수화
                messageBox
                    .alert([
                    errorCode === 235 ? t('device.warning') : t('message.error_apply_setting'),
                    `${t('device.err_code')}: ${errorCode}`,
                    `${t('device.description')}: ${errorMessage}`,
                ])
                    .open();
            }
        };
        const checkConnection = async () => {
            return await commonService.isConnection(deviceId.value);
        };
        // @TODO: storm cover관련 세팅도 필요 시 validation 추가
        const schema = yup.object().shape({
            backup_soc: yup
                .number()
                .integer()
                .test('backup_soc', { key: 'message.invalid' }, (value) => {
                if (genType.value === GEN_TYPE.GEN2) {
                    value = state.data?.backup_soc;
                    if (Helper.isNull(value)) {
                        if (state.data?.backup_soc) {
                            state.data.backup_soc = 0;
                        }
                    }
                }
                return true;
            }),
            battery_backup_soc: yup
                .number()
                .integer()
                .test('battery_backup_soc', { key: 'message.invalid' }, (value) => {
                if (genType.value !== GEN_TYPE.GEN2) {
                    value = state.data?.battery_backup_soc;
                    if (Helper.isNull(value)) {
                        if (state.data?.battery_backup_soc) {
                            state.data.battery_backup_soc = 0;
                        }
                    }
                }
                return true;
            }),
            energy_policy: yup.number().test('energy_policy', { key: 'message.invalid' }, function (value) {
                const { createError } = this;
                if (genType.value !== GEN_TYPE.GEN2) {
                    value = state.data?.energy_policy;
                    if (Helper.isNull(value)) {
                        return createError({
                            message: { key: 'message.field_required' },
                        });
                    }
                    return true;
                }
                return true;
            }),
        });
        const checkIsValidPricingSettingsParams = () => {
            if (state.energyPolicy !== null) {
                const { tou_start_date, tou_end_date, tou_weekday_section, tou_weekend_section } = state.energyPolicy;
                if (!tou_start_date || !tou_end_date) {
                    messageBox.alert(t('message.enter_application_period')).open();
                    return false;
                }
                const startDate = moment(tou_start_date, 'YYYYMMDD').toDate();
                const endDate = moment(tou_end_date, 'YYYYMMDD').toDate();
                if (!DateHelper.isValidDateRange(startDate, endDate)) {
                    messageBox.alert(t('message.start_before_end')).open();
                    return false;
                }
                if (_.isEmpty(tou_weekday_section)) {
                    messageBox.alert(t('message.invalid_weekdays')).open();
                    return false;
                }
                if (_.isEmpty(tou_weekend_section)) {
                    messageBox.alert(t('message.invalid_weekend')).open();
                    return false;
                }
            }
            return true;
        };
        const onEdit = async () => {
            const connectionStatus = await checkConnection();
            if (connectionStatus) {
                state.editable = true;
            }
            else {
                messageBox.alert(t('message.modify_when_disconnected')).open();
            }
        };
        const onCancel = () => {
            state.isInit = false;
            state.data = state.copyData;
            state.energyPolicy = state.copyData;
            state.batteryGen2 = { backup_soc: state.copyData?.backup_soc ?? 0 };
            state.batteryGen3 = { battery_backup_soc: state.copyData?.battery_backup_soc ?? 0 };
            state.stormCoverSetting = {
                storm_cover_flag: state.copyData?.storm_cover_flag,
                storm_cover_charge_type: state.copyData?.storm_cover_charge_type,
            };
            state.editable = false;
            nextTick(() => (state.isInit = true));
        };
        const onSave = () => {
            if (state.energyPolicy?.energy_policy === parseInt(ENERGY_POLICY.TIME_BASED, 10)) {
                if (!checkIsValidPricingSettingsParams())
                    return;
            }
            saveSetting();
            state.editable = false;
        };
        const getCurrentOperationMode = async () => {
            const deviceInfo = await deviceServiceUser.getDeviceProfile(siteId.value);
            if (!deviceInfo)
                return null;
            let [masterDeviceInfo] = deviceInfo.device_list;
            if (deviceInfo.device_list.length > 1) {
                const primaryACDeviceInfo = deviceInfo.device_list.find((device) => device.device_info.ems_type_cd === EMS_TYPE.PRIMARY);
                const standAloneACDeviceInfo = deviceInfo.device_list.find((device) => device.device_info.ems_type_cd === EMS_TYPE.STAND_ALONE);
                masterDeviceInfo = primaryACDeviceInfo ?? standAloneACDeviceInfo ?? masterDeviceInfo;
            }
            const energyControlValue = masterDeviceInfo.gen_type === GEN_TYPE.AC_SYS ? masterDeviceInfo.device_info.energy_control : '';
            return isEnergyPolicyType(energyControlValue) ? energyControlValue : null;
        };
        onMounted(async () => {
            if (isUS && isStormCoverSupportedDevice) {
                currentEnergyControl.value = await getCurrentOperationMode();
            }
            await getDeviceSettingInfo();
            state.isInit = true;
        });
        return {
            state,
            saveSetting,
            schema,
            onEdit,
            onCancel,
            onSave,
            isUS,
            isStormCoverSupportedDevice,
            isEnergyBackupSetting,
            GEN_TYPE,
            genType,
            currentEnergyControl,
            isSolarConfig,
            isACSystem,
        };
    },
});
